/**
 * Open a server-rendered PDF in a new tab.
 *
 * The API accepts only a Bearer token, never a cookie, so a plain `window.open(url)` reaches it
 * unauthenticated. This fetches with the same in-memory token `api()` uses, retries once after a
 * refresh on 401, and hands the browser a blob URL instead.
 *
 * The tab is opened BEFORE the fetch, synchronously inside the click, so a popup blocker still sees
 * a user gesture; it is pointed at the blob once the PDF arrives, and closed again if it fails.
 *
 * Because it is opened first, it would otherwise sit blank for as long as the render takes — and
 * the API starts a headless Chromium to make the PDF, so the first one after a restart is seconds,
 * not milliseconds. A blank tab reads as a broken link, so it is given a holding page to show in
 * the meantime.
 */
import { ApiError, refreshSession, tokens } from "@/lib/api-client";

/** How long a blob URL outlives the tab it was handed to. Long enough to load and print. */
const REVOKE_AFTER_MS = 60_000;

/**
 * The holding page. Inlined and dependency-free: this document is written into a tab that has no
 * origin of its own and can load nothing from ours. Colours are the light/dark surface and brand
 * tokens by value, since it cannot see the stylesheet either.
 */
const HOLDING_PAGE = `<!doctype html><html lang="en"><head><meta charset="utf-8">
<title>Preparing your document…</title><style>
:root{color-scheme:light dark}
body{margin:0;height:100vh;display:grid;place-content:center;justify-items:center;gap:1rem;
background:#f7faf9;color:#3b454e;
font:500 14px/1.5 Inter,system-ui,-apple-system,Segoe UI,sans-serif}
.bar{width:196px;height:3px;border-radius:999px;background:#eef2f1;overflow:hidden}
.bar i{display:block;width:40%;height:100%;border-radius:999px;background:#3f8b80;
animation:s 1.15s cubic-bezier(.65,0,.35,1) infinite}
@keyframes s{from{transform:translateX(-80%)}to{transform:translateX(230%)}}
@media (prefers-reduced-motion:reduce){.bar i{animation:none;width:100%;opacity:.6}}
@media (prefers-color-scheme:dark){body{background:#12171c;color:#e6ebef}.bar{background:#222a31}}
</style></head><body>
<div class="bar"><i></i></div>
<p role="status">Preparing your document…</p>
</body></html>`;

/**
 * Fetch a file with the Bearer token, retrying once after a refresh on 401. Also returns the file
 * name the server gave in `content-disposition`, for a download.
 */
export async function fetchBlob(
  path: string,
  accept = "*/*"
): Promise<{ blob: Blob; fileName: string | null; rows: number | null }> {
  const build = (): RequestInit => {
    const token = tokens.get();
    return {
      credentials: "include",
      headers: {
        accept,
        ...(token ? { authorization: `Bearer ${token}` } : {}),
      },
    };
  };
  let res = await fetch(`/api/v1${path}`, build());
  if (res.status === 401 && (await refreshSession()))
    res = await fetch(`/api/v1${path}`, build());
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as {
      error?: { code?: string; message?: string };
    } | null;
    throw new ApiError(
      res.status,
      body?.error?.code ?? "http_error",
      body?.error?.message ?? res.statusText
    );
  }
  const fileName = dispositionFileName(
    res.headers.get("content-disposition") ?? ""
  );
  // A list export says how many rows it holds (`x-row-count`); a workbook cannot be counted here.
  const counted = Number(res.headers.get("x-row-count") ?? "");
  return {
    blob: await res.blob(),
    fileName,
    rows:
      res.headers.has("x-row-count") && Number.isFinite(counted)
        ? counted
        : null,
  };
}

const fetchPdf = (path: string) => fetchBlob(path, "application/pdf");

/** The file name in a `content-disposition` header; RFC 5987's `filename*=` wins when both are sent. */
export function dispositionFileName(disposition: string): string | null {
  const extended = /filename\*=(?:UTF-8|ISO-8859-1)?'[^']*'([^;]+)/i.exec(
    disposition
  )?.[1];
  if (extended) {
    try {
      return decodeURIComponent(extended.trim());
    } catch {
      // A malformed escape: fall back to the plain name.
    }
  }
  return /(?:^|;)\s*filename="?([^";]+)"?/i.exec(disposition)?.[1] ?? null;
}

export function openPdf(path: string): Promise<void> {
  return openPdfAfter(() => Promise.resolve(path));
}

/**
 * Run `work` and then open the PDF at the path it returns: Issue a note, then print it. The tab is
 * still opened first, inside the click, so the popup blocker never sees a tab opened after an
 * `await`. If `work` (or the PDF) fails the tab is closed and the error is rethrown to the caller.
 */
export async function openPdfAfter(work: () => Promise<string>): Promise<void> {
  const tab = window.open("", "_blank");
  try {
    tab?.document.write(HOLDING_PAGE);
    tab?.document.close();
  } catch {
    // A tab we cannot write to still works; it is just blank until the blob lands.
  }
  try {
    const { blob } = await fetchPdf(await work());
    const url = URL.createObjectURL(blob);
    if (tab) tab.location.href = url;
    else window.location.assign(url);
    setTimeout(() => URL.revokeObjectURL(url), REVOKE_AFTER_MS);
  } catch (e) {
    tab?.close();
    throw e;
  }
}

/** Save a PDF to the operator's downloads, named as the server names it. No tab is opened. */
export const downloadPdf = (path: string, fallbackName: string) =>
  saveBlob(fetchPdf(path), fallbackName);

/** Save any file the API serves (a Feedback attachment, say), named as the server names it. */
export const downloadFile = (path: string, fallbackName: string) =>
  saveBlob(fetchBlob(path), fallbackName);

async function saveBlob(
  fetched: Promise<{ blob: Blob; fileName: string | null }>,
  fallbackName: string
): Promise<void> {
  const { blob, fileName } = await fetched;
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName ?? fallbackName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), REVOKE_AFTER_MS);
}
